import React, { useState } from 'react';
import { useInView } from '../hooks/useInView';

const experiences = [
  {
    role: 'Software Engineer',
    period: 'July 2024 – Present',
    type: 'Full-time',
    bullets: [
      'Designed and developed microservices integrated with Infor M3 ERP, improving operational efficiency and reducing manual workflows.',
      'Optimized Kafka-based event processing pipelines, significantly reducing consumer lag and improving throughput.',
      'Identified and resolved performance bottlenecks in Redis caching and CDC connectors, improving API response times.',
      'Collaborated with SRE teams to troubleshoot production issues, improving system uptime and reliability.',
      'Implemented structured logging and monitoring using New Relic, reducing debugging and incident resolution time.',
      'Enhanced application validation and user experience by working closely with ERP consultants.',
    ],
    tags: ['Kafka', 'Redis', 'NestJS', 'New Relic', 'Infor M3', 'Docker'],
  },
  {
    role: 'Associate Software Engineer',
    period: 'March 2022 – June 2024',
    type: 'Full-time',
    bullets: [
      'Developed backend services using NestJS and GraphQL, improving data efficiency and API performance.',
      'Implemented event-driven architecture using Kafka for scalable inter-service communication.',
      'Built responsive frontend components using Angular and Kendo UI, enhancing user productivity.',
      'Automated CI/CD pipelines using Jenkins, reducing manual deployment effort.',
      'Conducted root cause analysis (RCA) and fixed recurring production issues.',
      'Monitored system health (Kafka, Redis, CDC pipelines) to proactively prevent failures.',
    ],
    tags: ['NestJS', 'GraphQL', 'Angular', 'Kafka', 'Jenkins', 'Redis'],
  },
  {
    role: 'Software Engineering Intern',
    period: 'August 2020 – February 2021',
    type: 'Internship',
    bullets: [
      'Gained hands-on experience in Java Spring Boot and backend development.',
      'Developed and tested REST APIs following best practices.',
      'Wrote unit tests to improve code reliability and reduce defects.',
      'Worked with Docker and Linux environments to understand deployment workflows.',
    ],
    tags: ['Java', 'Spring Boot', 'REST APIs', 'Docker', 'Linux'],
  },
];

export default function Experience() {
  const [active, setActive] = useState(0);
  const [ref, inView] = useInView(0.1);

  return (
    <section id="experience" ref={ref} style={{
      padding: 'clamp(80px, 10vw, 140px) clamp(24px, 8vw, 120px)',
      background: 'var(--white)',
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ opacity: inView ? 1 : 0, transform: inView ? 'none' : 'translateY(24px)', transition: 'all 0.6s ease' }}>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--accent-warm)', marginBottom: 20 }}>
            02 — Experience
          </p>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 4vw, 52px)', lineHeight: 1.1, letterSpacing: '-0.02em', marginBottom: 60 }}>
            Where I've worked.
          </h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'clamp(160px, 22%, 240px) 1fr', gap: 0, opacity: inView ? 1 : 0, transition: 'opacity 0.6s ease 0.2s' }}>
          {/* Tab list */}
          <div style={{ borderRight: '1px solid var(--border)' }}>
            {experiences.map((exp, i) => (
              <button key={i} onClick={() => setActive(i)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none',
                  borderRight: active === i ? '2px solid var(--accent-warm)' : '2px solid transparent',
                  padding: '16px 24px', cursor: 'pointer',
                  background: active === i ? 'var(--accent-warm-light)' : 'transparent',
                  transition: 'all 0.2s',
                  marginRight: -1,
                }}>
                <p style={{
                  fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.04em',
                  color: active === i ? 'var(--accent-warm)' : 'var(--ink-3)',
                  fontWeight: active === i ? 500 : 300, transition: 'color 0.2s',
                }}>{exp.role}</p>
                <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--ink-4)', marginTop: 4 }}>{exp.type}</p>
              </button>
            ))}
          </div>

          {/* Content */}
          <div style={{ padding: '0 0 0 clamp(24px, 4vw, 56px)' }} key={active}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 28 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(22px, 2.5vw, 30px)', letterSpacing: '-0.01em' }}>
                {experiences[active].role}
              </h3>
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.08em',
                color: 'var(--ink-3)', background: 'var(--cream)', border: '1px solid var(--border)',
                padding: '6px 14px', borderRadius: 2, whiteSpace: 'nowrap',
              }}>{experiences[active].period}</span>
            </div>

            <ul style={{ listStyle: 'none', marginBottom: 36, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {experiences[active].bullets.map((b, i) => (
                <li key={i} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <span style={{ color: 'var(--accent-warm)', fontFamily: 'var(--font-mono)', fontSize: 13, marginTop: 2, flexShrink: 0 }}>→</span>
                  <p style={{ color: 'var(--ink-2)', lineHeight: 1.7, fontWeight: 300, fontSize: 15 }}>{b}</p>
                </li>
              ))}
            </ul>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {experiences[active].tags.map(tag => (
                <span key={tag} style={{
                  fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.06em',
                  color: 'var(--ink-2)', background: 'var(--cream)', border: '1px solid var(--border)',
                  padding: '5px 12px', borderRadius: 2,
                }}>{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 640px) {
          #experience > div > div:last-child {
            grid-template-columns: 1fr !important;
          }
          #experience > div > div:last-child > div:first-child {
            border-right: none !important;
            border-bottom: 1px solid var(--border);
            display: flex;
            flex-direction: row;
            overflow-x: auto;
          }
        }
      `}</style>
    </section>
  );
}
